import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UserRole } from '@app/core/auth/auth.repository';

export interface ChatMessage {
  t_ot_messagesid: number;
  message: string;
  created: string;
  sender_type: 'student' | 'supervisor' | 'twin' | 'system';
  sender_id: number | null;
  student_id: number;
  supervisor_id: number;
  student_course_id?: number;
  student_course_step_id?: number;
  subject?: string;
  status_id?: number; // Message status: 1 = delivered, 2 = read
  is_notification?: number; // 0 = chat message, 1 = system notification
  step_title?: string; // Title of the step (from API join with t_ot_step)
  step_sort?: number; // Step position/order within the course
  actiontype_id?: number; // Step action type (1=video, 4=essay, 5=drill, etc.)
  course_title?: string; // Title of the course (from API join with t_ot_course)
  attention_state?: 'needs_supervisor_action' | 'waiting_for_student' | 'new_message' | 'resolved' | string;
  last_reply_by?: string | null;
  last_reply_at?: string | null;
  ack_required?: number;
  ack_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  sender?: {
    name: string;
    avatar_url: string;
  };
  attached_files?: {
    id: number;
    uuid?: string;
    original_name: string;
    mime_type: string;
    file_size: number;
    is_image: number | boolean;
    url: string;
  }[];
}

/**
 * CommentsVersion interface - matches what phuture-comments expects.
 * Individual t_ot_message records are converted into this format for display.
 */
export interface CommentsVersionComment {
  id: number;
  sender_id: number;
  sender_role: UserRole;
  sender_name: string;
  message: string;
  timestamp: string;
  avatar: string;
  isDelivered: boolean;
  isRead: boolean;
}

export interface CommentsVersion {
  version_tag: number;
  timestamp: string;
  comments: CommentsVersionComment[];
  last_comment: CommentsVersionComment | null;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

@Injectable({ providedIn: 'root' })
export class ChatService {
  private messages$ = new BehaviorSubject<ChatMessage[]>([]);
  private pollingIntervalMs = 5000;

  errorMsg: string = '';

  constructor(private httpClient: HttpClient) {}

  /**
   * GET: all chat messages for a student
   */
  getMessages(studentId: number, page = 1, perPage = 50): Observable<ChatMessage[]> {
    const url = `${environment.serverUrl}/v1/messages/${studentId}`;

    const params = new HttpParams().set('page', page.toString()).set('per_page', perPage.toString());

    // Use text response type to handle malformed JSON
    return this.httpClient.get(url, { 
      headers: httpOptions.headers, 
      params,
      responseType: 'text'
    }).pipe(
      retry(2),
      map((textResponse: string) => {
        try {
          // Try to parse as JSON
          let jsonResponse: any;
          
          // Find the first '{' which should be the start of the JSON
          const jsonStart = textResponse.indexOf('{');
          
          if (jsonStart !== -1) {
            // Find the matching closing brace by counting braces
            let braceCount = 0;
            let jsonEnd = -1;
            
            for (let i = jsonStart; i < textResponse.length; i++) {
              if (textResponse[i] === '{') {
                braceCount++;
              } else if (textResponse[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i;
                  break;
                }
              }
            }
            
            if (jsonEnd !== -1 && jsonEnd > jsonStart) {
              const jsonString = textResponse.substring(jsonStart, jsonEnd + 1);
              jsonResponse = JSON.parse(jsonString);
            } else {
              // Fallback: try parsing the whole response
              jsonResponse = JSON.parse(textResponse);
            }
          } else {
            // No '{' found, try parsing the whole response
            jsonResponse = JSON.parse(textResponse);
          }
          
          // Handle different response formats
          if (jsonResponse && jsonResponse.data) {
            return jsonResponse.data;
          }
          // If response is already an array
          if (Array.isArray(jsonResponse)) {
            return jsonResponse;
          }
          // If response has a different structure
          if (jsonResponse && Array.isArray(jsonResponse.messages)) {
            return jsonResponse.messages;
          }
          console.warn('Unexpected response format:', jsonResponse);
          return [];
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          console.error('Response text (first 500 chars):', textResponse.substring(0, 500));
          console.error('Response text (last 500 chars):', textResponse.substring(Math.max(0, textResponse.length - 500)));
          // Return empty array on parse error
          return [];
        }
      }),
      catchError((err) => {
        // Log the raw response for debugging
        if (err.error && typeof err.error === 'string') {
          console.error('Raw error response (string, first 500):', err.error.substring(0, 500));
        } else if (err.error) {
          console.error('Error response object:', err.error);
        }
        return this.handleError(err);
      })
    );
  }

  /**
   * GET: chat messages for a student with pagination metadata.
   * Returns messages plus total/page/perPage for "Earlier messages" pagination.
   */
  getMessagesPage(
    studentId: number,
    page = 1,
    perPage = 20
  ): Observable<{ messages: ChatMessage[]; total: number; page: number; perPage: number }> {
    const url = `${environment.serverUrl}/v1/messages/${studentId}`;
    const params = new HttpParams().set('page', page.toString()).set('per_page', perPage.toString());

    return this.httpClient.get(url, {
      headers: httpOptions.headers,
      params,
      responseType: 'text',
    }).pipe(
      retry(2),
      map((textResponse: string) => {
        try {
          let jsonResponse: any;
          const jsonStart = textResponse.indexOf('{');
          if (jsonStart !== -1) {
            let braceCount = 0;
            let jsonEnd = -1;
            for (let i = jsonStart; i < textResponse.length; i++) {
              if (textResponse[i] === '{') braceCount++;
              else if (textResponse[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i;
                  break;
                }
              }
            }
            if (jsonEnd !== -1 && jsonEnd > jsonStart) {
              jsonResponse = JSON.parse(textResponse.substring(jsonStart, jsonEnd + 1));
            } else {
              jsonResponse = JSON.parse(textResponse);
            }
          } else {
            jsonResponse = JSON.parse(textResponse);
          }
          const data = jsonResponse?.data ?? (Array.isArray(jsonResponse) ? jsonResponse : jsonResponse?.messages ?? []);
          const total = Number(jsonResponse?.total ?? jsonResponse?.count ?? data?.length ?? 0);
          const pageNum = Number(jsonResponse?.page ?? page);
          const perPageNum = Number(jsonResponse?.per_page ?? perPage);
          return {
            messages: Array.isArray(data) ? data : [],
            total: total > 0 ? total : (Array.isArray(data) ? data.length : 0),
            page: pageNum,
            perPage: perPageNum,
          };
        } catch (parseError) {
          console.error('getMessagesPage JSON parse error:', parseError);
          return { messages: [], total: 0, page, perPage };
        }
      }),
      catchError((err) => {
        if (err.error && typeof err.error === 'string') {
          console.error('getMessagesPage raw error (first 500):', err.error.substring(0, 500));
        }
        return this.handleError(err);
      })
    );
  }

  /**
   * GET: all chat/comment messages for a specific student course step
   * Uses the /v1/messages/by-step/{student_course_step_id} endpoint
   */
  getMessagesByStep(studentCourseStepId: number): Observable<ChatMessage[]> {
    if (!studentCourseStepId || studentCourseStepId <= 0) {
      return of([]);
    }

    const url = `${environment.serverUrl}/v1/messages/by-step/${studentCourseStepId}`;

    return this.httpClient.get<any>(url, { headers: httpOptions.headers }).pipe(
      map((response: any) => {
        if (response?.status === 'OK' && Array.isArray(response.data)) {
          // Filter out system notifications and messages with no content
          return (response.data as ChatMessage[]).filter(m => !m.is_notification && (m.message?.trim().length > 0 || m.subject?.trim().length > 0));
        }
        return [];
      }),
      catchError((err) => {
        console.warn('Failed to fetch messages by step:', err);
        return of([]);
      })
    );
  }

  markMessageAsRead(messageId: number): Observable<any> {
    const url = `${environment.serverUrl}/v1/messages/read/${messageId}`;
    return this.httpClient.post(url, {}, httpOptions).pipe(
      tap(() => console.log(`Marked message ${messageId} as read`)),
      catchError((err) => this.handleError(err))
    );
  }

  markAllAsRead(studentId: number): Observable<any> {
    const url = `${environment.serverUrl}/v1/messages/mark-all-read/${studentId}`;
    return this.httpClient.post(url, {}, httpOptions).pipe(
      tap(() => console.log(`Marked all messages as read for student ${studentId}`)),
      catchError((err) => this.handleError(err))
    );
  }

  markAsReadByStep(studentCourseStepId: number): Observable<any> {
    const url = `${environment.serverUrl}/v1/messages/mark-all-read-by-step/${studentCourseStepId}`;
    return this.httpClient.post(url, {}, httpOptions).pipe(
      tap(() => console.log(`Marked all messages as read for step ${studentCourseStepId}`)),
      catchError((err) => this.handleError(err))
    );
  }

  /**
   * GET unread message counts grouped by student_course_step_id for a given course.
   * Uses GET /v1/messages/unread-counts-by-step/{student_course_id}
   * Returns a Record<stepId, count>.
   */
  getUnreadCountsByStep(studentCourseId: number): Observable<Record<number, number>> {
    const url = `${environment.serverUrl}/v1/messages/unread-counts-by-step/${studentCourseId}`;
    return this.httpClient.get<any>(url, httpOptions).pipe(
      map((res: any) => {
        const raw: Record<string, number> = res?.data || {};
        const counts: Record<number, number> = {};
        Object.entries(raw).forEach(([k, v]) => {
          const id = Number(k);
          if (id > 0) counts[id] = Number(v) || 0;
        });
        return counts;
      }),
      catchError((err) => {
        console.warn('getUnreadCountsByStep error', err);
        return of({} as Record<number, number>);
      })
    );
  }

  /**
   * POST: send new message.
   * Uses responseType:'text' + manual JSON extraction so that PHP debug output
   * appended after the JSON body does not cause a parse failure.
   */
  sendMessage(payload: Partial<ChatMessage>): Observable<any> {
    const url = `${environment.serverUrl}/v1/messages`;

    return this.httpClient.post(url, JSON.stringify(payload), {
      headers: httpOptions.headers,
      responseType: 'text',
    }).pipe(
      map((textResponse: string) => {
        const jsonStart = textResponse.indexOf('{');
        if (jsonStart === -1) return {};
        let braceCount = 0;
        let jsonEnd = -1;
        for (let i = jsonStart; i < textResponse.length; i++) {
          if (textResponse[i] === '{') braceCount++;
          else if (textResponse[i] === '}') { braceCount--; if (braceCount === 0) { jsonEnd = i; break; } }
        }
        const jsonStr = jsonEnd !== -1 ? textResponse.substring(jsonStart, jsonEnd + 1) : textResponse;
        try { return JSON.parse(jsonStr); } catch { return {}; }
      }),
      tap((response) => console.log('Message sent:', response)),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Start polling every X seconds
   */
  startPolling(studentId: number): Observable<ChatMessage[]> {
    return interval(this.pollingIntervalMs).pipe(
      switchMap(() => this.getMessages(studentId)),
      tap((msgs) => this.messages$.next(msgs))
    );
  }

  get messagesStream$(): Observable<ChatMessage[]> {
    return this.messages$.asObservable();
  }

  setMessagesImmediately(messages: ChatMessage[]) {
    this.messages$.next(messages);
  }

  private handleError(error: HttpErrorResponse) {
    console.log('ChatService error handler', error);
    console.log('Error details:', {
      status: error.status,
      statusText: error.statusText,
      error: error.error,
      message: error.message,
      url: error.url
    });
    if (error.error instanceof ErrorEvent) {
      this.errorMsg = `Error: ${error.error.message}`;
    } else {
      // Better error message extraction
      const errorDetail = error.error?.error || error.error?.message || JSON.stringify(error.error) || error.message;
      this.errorMsg = this.getServerErrorMessage(error, errorDetail);
    }
    return throwError(() => this.errorMsg);
  }

  private getServerErrorMessage(error: HttpErrorResponse, errorDetail?: string) {
    switch (error.status) {
      case 0:
        return `Client/network error: ${errorDetail || error.error}`;
      case 404:
        return `Not Found: ${errorDetail || error.message}`;
      case 403:
        return `Access Denied: ${errorDetail || error.message}`;
      case 500:
        return `Internal Server Error: ${errorDetail || error.message}`;
      default:
        return `Unknown Server Error: ${errorDetail || JSON.stringify(error.error) || error.message}`;
    }
  }

  // ─────────────────────────────────────────────────────
  //  HELPERS: Convert t_ot_message records ↔ CommentsVersion
  // ─────────────────────────────────────────────────────

  /**
   * Convert an array of t_ot_message records (from API) into a CommentsVersion
   * object that the phuture-comments web component can display.
   */
  buildCommentsVersionFromMessages(messages: ChatMessage[]): CommentsVersion {
    // Deduplicate messages (old JSON migration created duplicate DB rows)
    const seen = new Set<string>();
    const uniqueMessages = (messages || []).filter(msg => {
      const key = `${msg.message}|${msg.sender_type}|${msg.created}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const comments: CommentsVersionComment[] = uniqueMessages.map((msg) => ({
      id: msg.t_ot_messagesid || 0,
      sender_id: msg.sender_id || 0,
      sender_role: msg.sender_type === 'supervisor' ? UserRole.Supervisor : UserRole.Student,
      sender_name: msg.sender?.name || (msg.sender_type === 'supervisor' ? 'Supervisor' : 'Student'),
      message: msg.message || '',
      timestamp: msg.created || new Date().toISOString(),
      avatar: msg.sender?.avatar_url || '',
      isDelivered: true,
      isRead: (msg.status_id || 1) >= 2,
    }));

    return {
      version_tag: comments.length,
      timestamp: new Date().toISOString(),
      comments,
      last_comment: comments.length > 0 ? comments[comments.length - 1] : null,
    };
  }

  /**
   * Convenience: Load step messages from API and return as CommentsVersion JSON string
   * (ready to pass to [comments] input of phuture-comments).
   */
  loadStepCommentsAsVersion(studentCourseStepId: number): Observable<CommentsVersion> {
    return this.getMessagesByStep(studentCourseStepId).pipe(
      map((msgs) => this.buildCommentsVersionFromMessages(msgs))
    );
  }
}
